import { FieldProps, FormControl, useFormField } from '@core/utils/form';
import { R } from '@core/utils/r';
import { TimeInput, toMoment } from '@core/utils/time';
import FormHelperText from '@mui/material/FormHelperText';

import moment from 'moment';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { FormGroup, Label } from 'reactstrap';
import { DatePicker } from 'antd';
import type { Moment } from 'moment';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import { DATE_FORMAT } from '@business/constants/time';
const MomentDatePicker = DatePicker.generatePicker<Moment>(momentGenerateConfig);
const { RangePicker } = MomentDatePicker;

export interface FormDateRangeInputProps extends FieldProps<FormControl<string> | FormControl<string | null>> {
  minDate?: TimeInput;
  maxDate?: TimeInput;
  onChange?(range: string): void;
  appendToBody?: boolean;
  anchorDirection?: 'left' | 'right';
  withoutGroup?: boolean;
  small?: boolean;
}

export function FormDateRange(props: FormDateRangeInputProps) {
  const { formControl, fieldState, fieldConfig } = useFormField(props);

  const fieldValue = fieldState.value;
  const sStart = R.toNumber(R.toString(fieldValue).split('-')[0]);
  const sEnd = R.toNumber(R.toString(fieldValue).split('-')[1]);

  const startDate = useMemo(() => {
    if (sStart) {
      return toMoment(sStart).startOf('day');
    } else {
      return toMoment().subtract(30, 'day').startOf('day');
    }
  }, [sStart]);
  const endDate = useMemo(() => {
    if (sEnd) {
      return toMoment(sEnd).endOf('day');
    } else {
      return toMoment().endOf('day');
    }
  }, [sEnd]);

  const fieldTouched = (fieldState && fieldState.touched) || formControl.inheritedSubmitted || false;

  const fieldError = (fieldTouched && fieldState.errorMessage) || null;

  const fieldReadonly = fieldConfig.readonly || false;
  const fieldDisabled = fieldConfig.disabled || false;

  const fieldPlaceholder = fieldConfig.placeholder || 'mm/dd/yyyy';

  const handleChange = useCallback(
    (startDate: moment.Moment | null, endDate: moment.Moment | null) => {
      const fieldValue = `${startDate?.startOf('day').unix()}-${endDate?.endOf('day').unix()}`;
      formControl.setValue(fieldValue);
      props.onChange && props.onChange(fieldValue);
    },
    [formControl, props],
  );

  const content = (
    <Fragment>
      {fieldConfig.label && (
        <>
          <Label for={formControl.uid}>{fieldConfig.label}</Label> <br />
        </>
      )}
      <RangePicker
        format={DATE_FORMAT}
        disabled={fieldDisabled}
        readOnly={fieldReadonly}
        allowClear={false}
        value={[startDate, endDate]}
        onChange={values => handleChange(values[0], values[1])}
        suffixIcon={null}
      />

      {!!fieldError && (
        <FormHelperText className="mui-error" style={{ color: '#f44336' }}>
          {fieldError}
        </FormHelperText>
      )}
    </Fragment>
  );

  if (props.withoutGroup) {
    return <div className="theme_date-picker">{content}</div>;
  }

  return <FormGroup className="theme_date-picker">{content}</FormGroup>;
}
